/**
 * @file: context.tsx
 *
 * The floating-chat launcher context. `useFloatingChat().openChat(query)`
 * resolves a conversation handle (room / contact / member) and opens it in a
 * docked floating panel — usable from anywhere (a task assignee chip, a
 * contact row, …) without the inbox.
 *
 * The state lives on `FloatingChatProvider` (mounted app-wide so any button can
 * open a chat); the panel itself is rendered by `FloatingChatHost` INSIDE the
 * navigation container (the chat conversation's coordinator uses
 * `useNavigation`), reading `active$` off this context.
 */

import { createContext, useContext } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatConversationHandle } from '../conversation/handle';
import { ConversationHandleQuery } from '../conversation/types';

export interface FloatingChatSelection {
  handle: ChatConversationHandle;
  /** Stable identity captured at open — keeps the conversation mounted across
   * a bare→baked materialisation (so only the title updates, no remount). */
  key: string;
}

export interface FloatingChatContextValue {
  /** Resolve the entry (spinner while resolving, alert on failure — a broken
   * room never renders) and open the floating chat panel on success. */
  openChat: (query: ConversationHandleQuery) => void;
  /** The open conversation, or `null` when closed. Read by `FloatingChatHost`. */
  active$: BehaviorSubject<FloatingChatSelection | null>;
}

export const FloatingChatContext =
  createContext<FloatingChatContextValue | null>(null);

export function useFloatingChat(): FloatingChatContextValue {
  const ctx = useContext(FloatingChatContext);
  if (ctx == null) {
    throw new Error(
      'useFloatingChat must be used within a FloatingChatProvider',
    );
  }
  return ctx;
}
